import React, { useState, useEffect } from "react";
import { db } from "../lib/instant";
import { useSearchParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { Search as SearchIcon, X } from "lucide-react";

export default function Search({ theme }) {
  const primaryColor   = theme?.primaryColor   || "#6b2118";
  const accentColor    = theme?.accentColor    || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";

  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(query);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (debouncedQ) setSearchParams({ q: debouncedQ });
    else setSearchParams({});
  }, [debouncedQ]);

  const { data } = db.useQuery({ products: { $: { where: { active: true } } } });
  const all = data?.products || [];

  const results = debouncedQ
    ? all.filter(p => {
        const q = debouncedQ.toLowerCase();
        return (
          (p.name       || "").toLowerCase().includes(q) ||
          (p.shortDesc  || "").toLowerCase().includes(q) ||
          (p.description|| "").toLowerCase().includes(q) ||
          (p.ingredients|| "").toLowerCase().includes(q) ||
          (p.weight     || "").toLowerCase().includes(q)
        );
      })
    : [];

  const suggestions = ["health mix", "500g", "1kg", "multigrain", "natural", "bulk"];

  return (
    <div style={{ paddingTop:64, minHeight:"100vh", background:"#fdf6e3" }}>
      {/* Search bar */}
      <div style={{ background: primaryColor, padding:"48px 5vw 56px" }}>
        <div style={{ maxWidth:640, margin:"0 auto", textAlign:"center" }}>
          <div style={{ fontSize:"0.72rem", letterSpacing:4, textTransform:"uppercase", color: accentColor, fontWeight:700, marginBottom:10 }}>Search</div>
          <h1 style={{ fontFamily:"Georgia, serif", fontSize:"clamp(1.6rem,4vw,2.6rem)", color:"#fff", marginBottom:28 }}>Find Products</h1>
          <div style={{ position:"relative" }}>
            <SearchIcon size={20} style={{ position:"absolute", left:18, top:"50%", transform:"translateY(-50%)", color:"#aaa" }} />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search for health mix, ingredients..."
              style={{
                width:"100%", padding:"16px 48px 16px 52px",
                border:"none", borderRadius:14, fontSize:"1rem",
                outline:"none", fontFamily:"inherit",
                boxShadow:"0 4px 24px rgba(0,0,0,0.2)",
              }}
            />
            {query && (
              <button onClick={() => setQuery("")} style={{ position:"absolute", right:16, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#aaa" }}>
                <X size={18}/>
              </button>
            )}
          </div>
          {/* Quick suggestions */}
          {!query && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", justifyContent:"center", marginTop:16 }}>
              {suggestions.map(s => (
                <button key={s} onClick={() => setQuery(s)} style={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)", color:"rgba(255,255,255,0.85)", padding:"6px 16px", borderRadius:40, fontSize:"0.78rem", cursor:"pointer" }}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth:1200, margin:"0 auto", padding:"40px 5vw" }}>
        {!debouncedQ ? (
          /* No query — show all products */
          <div>
            <div style={{ marginBottom:24, color:"#888", fontSize:"0.9rem" }}>Showing all {all.length} products</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:24 }}>
              {all.map(p => <ProductCard key={p.id} product={p} theme={theme} />)}
            </div>
          </div>
        ) : results.length === 0 ? (
          /* No results */
          <div style={{ textAlign:"center", padding:"80px 0" }}>
            <div style={{ fontSize:"4rem", marginBottom:16 }}>🔍</div>
            <h2 style={{ fontFamily:"Georgia, serif", color:"#2c1810", marginBottom:8 }}>No results for "{debouncedQ}"</h2>
            <p style={{ color:"#aaa", marginBottom:28 }}>Try a different keyword or browse all products.</p>
            <Link to="/shop" style={{ background: primaryColor, color:"#fff", padding:"12px 32px", borderRadius:40, textDecoration:"none", fontWeight:700 }}>Browse All Products</Link>
          </div>
        ) : (
          /* Results */
          <div>
            <div style={{ marginBottom:24, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
              <div>
                <span style={{ fontWeight:700, color:primaryColor, fontSize:"1.1rem" }}>{results.length}</span>
                <span style={{ color:"#888", fontSize:"0.9rem" }}> result{results.length!==1?"s":""} for "<strong style={{ color:"#333" }}>{debouncedQ}</strong>"</span>
              </div>
              <button onClick={() => setQuery("")} style={{ background:"none", border:`1px solid ${primaryColor}`, color:primaryColor, padding:"6px 14px", borderRadius:20, cursor:"pointer", fontSize:"0.8rem", fontWeight:600 }}>
                Clear Search
              </button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:24 }}>
              {results.map(p => <ProductCard key={p.id} product={p} theme={theme} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
